import type {
  SectionScopeAtRisk,
  StructureAtRiskNode,
  StructureRemapEntry,
  StructureRemapProblem,
  StructureRemapProblemReason,
  StructureTargetSection,
  UpgradeDriftEntry,
} from "@keydials/api-client";
import { DEFAULT_ITEM_NOUN, type ItemNoun, type SegmentNoun } from "../vocabulary";

// KEY-807 (structure remap 5/5): everything the upgrade notice's mapping table decides, kept free of React so web-user's notice
// and web-admin's on-behalf version agree, and so it can be tested. The server stays the judge (KEY-805 validates and dry-runs
// every save); this only has to offer choices it will accept and say plainly what each one does.

/** What the dial admin has picked for one row. Targets are paths in the NEW version. */
export type StructureChoice =
  | { action: "MoveTo"; sectionKey: string; segmentKey: string; itemKey?: string | null }
  | { action: "KeepAsCustomUnder"; sectionKey: string; segmentKey?: string | null; newName?: string | null }
  | { action: "Discard" };

/** One choice per row, by `structureRowId`. A row with no entry has no answer yet. */
export type StructureSelection = Record<string, StructureChoice>;

type NodePath = { sectionKey: string; segmentKey: string; itemKey?: string | null };

/** A row's id: its node's path. Lists have no item key. */
export function structureRowId(n: NodePath): string {
  return `${n.sectionKey}/${n.segmentKey}/${n.itemKey ?? ""}`;
}

const isItem = (n: NodePath) => !!n.itemKey;
const listRowId = (n: NodePath) => structureRowId({ sectionKey: n.sectionKey, segmentKey: n.segmentKey });

const plural = (n: number, one: string, many: string) => `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;

// ---- The dial's own words -------------------------------------------------------------------------------------------------

/** What the notice calls a list when the dial doesn't say. The table has always said "list"; it never says "segment". */
export const LIST_NOUN: SegmentNoun = { one: "list", many: "lists", One: "List", Many: "Lists" };

/**
 * The dial's words for its lists and items: `list` is the definition's segment noun ("list", "service"), `itemFor` a section's
 * item noun ("appointment"). Either may be left out, falling back to "list" and "item".
 */
export interface StructureNouns {
  list?: SegmentNoun;
  itemFor?: (sectionKey: string) => ItemNoun;
}

const listNoun = (nouns?: StructureNouns) => nouns?.list ?? LIST_NOUN;
const itemNounIn = (sectionKey: string | null | undefined, nouns?: StructureNouns) =>
  (sectionKey != null ? nouns?.itemFor?.(sectionKey) : undefined) ?? DEFAULT_ITEM_NOUN;

/**
 * One line of the "what's changing" list: "New list: Dentist", "Removed section: 6. Birthdays & Christmas". Lists and items are
 * named in the dial's own words, never "segment". Shared by web-user's notice and web-admin's Rollout preview.
 */
export function describeUpgradeEntry(e: UpgradeDriftEntry, nouns?: { list?: SegmentNoun; item?: ItemNoun }): string {
  const noun =
    e.level === "Section" ? "section"
    : e.level === "Segment" ? (nouns?.list ?? LIST_NOUN).one
    : e.level === "Item" ? (nouns?.item ?? DEFAULT_ITEM_NOUN).one
    : e.level === "Dimension" ? "measure"
    : e.level === "Attribute" ? "field"
    : String(e.level).toLowerCase();
  const verb = e.change === "Added" ? "New" : e.change === "Removed" ? "Removed" : "Changed";
  return `${verb} ${noun}: ${e.label}`;
}

// ---- Naming a place -------------------------------------------------------------------------------------------------------

// "8. Christmas" and "Christmas" are the same name for this purpose: section numbering isn't part of it.
const bare = (s: string) => norm(s).replace(/^\d+\s*[.)]\s*/, "");

/**
 * A list's place as the dial admin reads it: "5. Home & vehicles › Chores", or just "8. Christmas" when the list shares its
 * section's name or is the section's only list. No dangling "›" when there's no list to name.
 */
export function placeName(sectionLabel: string, listLabel?: string | null, onlyList = false): string {
  const list = listLabel?.trim();
  if (!list) return sectionLabel;
  if (onlyList || bare(list) === bare(sectionLabel)) return sectionLabel;
  return `${sectionLabel} › ${list}`;
}

/** A row's title: "Christmas (in 6. Birthdays & Christmas)", so the dial admin knows where it lived. Items under a listed list need no more. */
export function rowTitle(n: StructureAtRiskNode, atRisk: StructureAtRiskNode[] = []): string {
  if (!isItem(n)) return n.sectionLabel ? `${n.label} (in ${n.sectionLabel})` : n.label;
  const underListedList = atRisk.some((l) => !isItem(l) && l.sectionKey === n.sectionKey && l.segmentKey === n.segmentKey);
  if (underListedList || !n.sectionLabel) return n.label;
  return `${n.label} (in ${placeName(n.sectionLabel, n.listLabel)})`;
}

/**
 * What a removed node holds, in a parent's words: "2 dates, 3 files, 5 messages". Never blank: a node holding only messages
 * still says so. A list listed only for its items (its own status untouched) describes them instead, given `atRisk`:
 * "5 items with statuses you've set (all N/A)".
 */
export function describeHoldings(n: StructureAtRiskNode, atRisk: StructureAtRiskNode[] = [], nouns?: StructureNouns): string {
  if (!isItem(n) && !n.holdsData) {
    const inside = carriedItems(n, atRisk).filter((i) => i.holdsData);
    if (inside.length > 0) return describeItemsInside(n, inside, nouns);
  }
  const parts: string[] = [];
  if (n.changedStatusCount) parts.push(plural(n.changedStatusCount, "status set", "statuses set"));
  if (n.reviewDateCount) parts.push(plural(n.reviewDateCount, "date", "dates"));
  if (n.fieldCount) parts.push(plural(n.fieldCount, "detail filled in", "details filled in"));
  if (n.documentCount) parts.push(plural(n.documentCount, "file", "files"));
  if (n.messageCount) parts.push(plural(n.messageCount, "message", "messages"));
  else if (n.conversationCount) parts.push(plural(n.conversationCount, "conversation", "conversations"));
  if (n.historyCount) parts.push(plural(n.historyCount, "past update", "past updates"));
  if (n.subscriptionCount) parts.push(plural(n.subscriptionCount, "notification setting", "notification settings"));
  if (n.grantCount) parts.push(plural(n.grantCount, "person given access", "people given access"));
  if (parts.length > 0) return parts.join(", ");
  return n.holdsData ? "Something you've recorded" : "What's inside it holds things you've recorded";
}

/** The items a list carries, added up: how many have a status set (and to what, when it's one status), then the rest by kind. */
function describeItemsInside(list: StructureAtRiskNode, items: StructureAtRiskNode[], nouns?: StructureNouns): string {
  const noun = itemNounIn(list.sectionKey, nouns);
  const sum = (f: (i: StructureAtRiskNode) => number) => items.reduce((t, i) => t + f(i), 0);
  const parts: string[] = [];
  const withStatus = items.filter((i) => i.changedStatusCount > 0);
  if (withStatus.length > 0) {
    const names = withStatus.map((i) => i.changedStatusName?.trim() ?? "");
    const one = names[0] && names.every((x) => x === names[0]) ? names[0] : null;
    const which = one ? (withStatus.length === 1 ? ` (${one})` : ` (all ${one})`) : "";
    parts.push(`${plural(withStatus.length, noun.one, noun.many)} with ${withStatus.length === 1 ? "a status" : "statuses"} you've set${which}`);
  }
  const dates = sum((i) => i.reviewDateCount);
  const fields = sum((i) => i.fieldCount);
  const files = sum((i) => i.documentCount);
  const messages = sum((i) => i.messageCount);
  const threads = sum((i) => i.conversationCount);
  if (dates) parts.push(plural(dates, "date", "dates"));
  if (fields) parts.push(plural(fields, "detail filled in", "details filled in"));
  if (files) parts.push(plural(files, "file", "files"));
  if (messages) parts.push(plural(messages, "message", "messages"));
  else if (threads) parts.push(plural(threads, "conversation", "conversations"));
  // A status set writes history, so past updates only say something new when no status was set.
  const history = sum((i) => i.historyCount);
  if (history && withStatus.length === 0) parts.push(plural(history, "past update", "past updates"));
  if (parts.length > 0) return parts.join(", ");
  return `${plural(items.length, noun.one, noun.many)} with things you've recorded`;
}

/**
 * The rows that need the dial admin's answer: every listed list (answering it answers what's inside), and every item holding
 * data whose list isn't listed. Items a listed list carries are not counted again.
 */
export function decisionRows(atRisk: StructureAtRiskNode[]): StructureAtRiskNode[] {
  const lists = atRisk.filter((n) => !isItem(n));
  const listed = new Set(lists.map(listRowId));
  return [...lists, ...atRisk.filter((n) => isItem(n) && n.holdsData && !listed.has(listRowId(n)))];
}

/**
 * The notice's headline for the mapping table, counting the rows that need an answer: "This version reorganises your dial.
 * 1 list you've used is moving — choose where it goes so you keep what you've recorded."
 */
export function structureHeadline(atRisk: StructureAtRiskNode[], nouns?: StructureNouns): string {
  const rows = decisionRows(atRisk);
  const lists = rows.filter((n) => !isItem(n)).length;
  const items = rows.filter(isItem);
  if (rows.length === 0) return "This version reorganises your dial.";
  const sections = new Set(items.map((i) => i.sectionKey));
  const itemNoun = sections.size === 1 ? itemNounIn(items[0].sectionKey, nouns) : DEFAULT_ITEM_NOUN;
  const what = [
    lists ? plural(lists, listNoun(nouns).one, listNoun(nouns).many) : null,
    items.length ? plural(items.length, itemNoun.one, itemNoun.many) : null,
  ].filter(Boolean).join(" and ");
  const one = rows.length === 1;
  return `This version reorganises your dial. ${what} you've used ${one ? "is" : "are"} moving — choose where ${one ? "it goes" : "they go"} so you keep what you've recorded.`;
}

/** "Notification and sharing settings on 'Appointments' will be removed…": what a removed section's own settings become. */
export function describeSectionScopes(s: SectionScopeAtRisk): string {
  const kinds: string[] = [];
  if (s.subscriptionCount) kinds.push("Notification");
  if (s.grantCount || s.visibilityCount) kinds.push(kinds.length ? "sharing" : "Sharing");
  const what = kinds.length ? kinds.join(" and ") : "Some";
  return `${what} settings on '${s.label}' will be removed, unless everything you keep from it goes into the same section.`;
}

/** The line for a node a feed shares. Feeds never follow a move, so it leaves the feed whatever is chosen. */
export function describeLeavesFeeds(n: StructureAtRiskNode, sharedWith?: string | null): string {
  return sharedWith ? `'${n.label}' will stop being shared with ${sharedWith}.` : `'${n.label}' is shared with another organisation. That sharing stops with this update, whatever you choose.`;
}

// ---- Coverage (mirrors StructureRemapCoverage on the server) -------------------------------------------------------------

/**
 * Whether an item row is answered by its list's row: moving or keeping a list carries its items, so their own rows are hidden.
 * A discarded list covers them too, but they stay on show, because an item may still be kept from a list that goes.
 */
export function carriedByList(n: NodePath, selection: StructureSelection): StructureChoice | null {
  if (!isItem(n)) return null;
  const parent = selection[listRowId(n)];
  return parent && parent.action !== "Discard" ? parent : null;
}

/** Whether the item row is answered by its list being let go (a choice of its own still wins). */
export function goesWithList(n: NodePath, selection: StructureSelection): boolean {
  return isItem(n) && selection[listRowId(n)]?.action === "Discard";
}

/**
 * The nodes holding data that `selection` leaves unanswered, with the server's rule: an item is answered by its own choice or
 * its list's (any action, Discard included), and a list that holds nothing of its own needs no answer of its own.
 */
export function unansweredRows(atRisk: StructureAtRiskNode[], selection: StructureSelection): StructureAtRiskNode[] {
  return atRisk.filter((n) => n.holdsData && !selection[structureRowId(n)] && !(isItem(n) && selection[listRowId(n)]));
}

/** The rows the table shows, lists first with their items under them; an item under a moved or kept list is left out. */
export function visibleRows(atRisk: StructureAtRiskNode[], selection: StructureSelection): StructureAtRiskNode[] {
  const lists = atRisk.filter((n) => !isItem(n));
  const items = atRisk.filter(isItem);
  const out: StructureAtRiskNode[] = [];
  const placed = new Set<string>();
  for (const l of lists) {
    out.push(l);
    for (const i of items) {
      if (i.sectionKey === l.sectionKey && i.segmentKey === l.segmentKey) {
        placed.add(structureRowId(i));
        if (!carriedByList(i, selection)) out.push(i);
      }
    }
  }
  // An item whose list isn't listed (the list itself survives, only the item goes) has no parent row.
  for (const i of items) if (!placed.has(structureRowId(i))) out.push(i);
  return out;
}

/** How many items a list row carries with it when it moves or is kept. */
export function carriedItems(list: StructureAtRiskNode, atRisk: StructureAtRiskNode[]): StructureAtRiskNode[] {
  return atRisk.filter((n) => isItem(n) && n.sectionKey === list.sectionKey && n.segmentKey === list.segmentKey);
}

// ---- Choices ↔ the saved map --------------------------------------------------------------------------------------------

/** The saved map as a selection, so a reopened notice shows what was chosen. */
export function selectionFromMap(map: StructureRemapEntry[]): StructureSelection {
  const out: StructureSelection = {};
  for (const e of map) {
    const id = structureRowId(e);
    if (e.action === "MoveTo" && e.targetSectionKey && e.targetSegmentKey) {
      out[id] = { action: "MoveTo", sectionKey: e.targetSectionKey, segmentKey: e.targetSegmentKey, itemKey: e.targetItemKey ?? null };
    } else if (e.action === "KeepAsCustomUnder" && e.targetSectionKey) {
      out[id] = { action: "KeepAsCustomUnder", sectionKey: e.targetSectionKey, segmentKey: e.targetSegmentKey ?? null, newName: e.newName ?? null };
    } else if (e.action === "Discard") {
      out[id] = { action: "Discard" };
    }
  }
  return out;
}

/** Whether a choice names everything its action needs (a target, at the right level for the row). */
export function isChoiceComplete(n: NodePath, c: StructureChoice): boolean {
  if (c.action === "Discard") return true;
  if (c.action === "MoveTo") return !!c.sectionKey && !!c.segmentKey && (isItem(n) ? !!c.itemKey : !c.itemKey);
  return !!c.sectionKey && (isItem(n) ? !!c.segmentKey : !c.segmentKey);
}

/**
 * The map to save: one entry per complete choice on a row still at risk. Items carried by a moved or kept list send nothing of
 * their own (their list's entry answers them); half-made choices (an action with no target yet) are left out, so a partial
 * save never earns a 400 for them.
 */
export function buildStructureMap(atRisk: StructureAtRiskNode[], selection: StructureSelection): StructureRemapEntry[] {
  const out: StructureRemapEntry[] = [];
  for (const n of atRisk) {
    const c = selection[structureRowId(n)];
    if (!c || carriedByList(n, selection) || !isChoiceComplete(n, c)) continue;
    const base = { sectionKey: n.sectionKey, segmentKey: n.segmentKey, itemKey: n.itemKey ?? null };
    if (c.action === "Discard") out.push({ ...base, action: "Discard" });
    else if (c.action === "MoveTo") {
      out.push({ ...base, action: "MoveTo", targetSectionKey: c.sectionKey, targetSegmentKey: c.segmentKey, targetItemKey: c.itemKey ?? null });
    } else {
      const name = c.newName?.trim();
      out.push({
        ...base, action: "KeepAsCustomUnder", targetSectionKey: c.sectionKey,
        targetSegmentKey: isItem(n) ? (c.segmentKey ?? null) : null, newName: name ? name : null,
      });
    }
  }
  return out;
}

/** Rows whose moves land on the same node, which the server refuses (ManyToOne). Keyed by row id, naming the other row. */
export function moveCollisions(atRisk: StructureAtRiskNode[], selection: StructureSelection): Record<string, string> {
  const byTarget = new Map<string, StructureAtRiskNode>();
  const out: Record<string, string> = {};
  for (const n of atRisk) {
    const c = selection[structureRowId(n)];
    if (!c || c.action !== "MoveTo" || carriedByList(n, selection) || !isChoiceComplete(n, c)) continue;
    const key = `${c.sectionKey}/${c.segmentKey}/${c.itemKey ?? ""}`;
    const first = byTarget.get(key);
    if (first) out[structureRowId(n)] = first.label;
    else byTarget.set(key, n);
  }
  return out;
}

// ---- Suggestions ----------------------------------------------------------------------------------------------------------

const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-GB");

// "Dentist · Mila", "Dentist - Mila", "Dentist: Mila" and the like: a name that leads with the place it belongs.
const LEADING_SEPARATOR = /^(.+?)\s*(?:·|•|–|—|:|\/|\s-\s|\|)\s*(.+)$/;

/** Splits "Dentist · Mila" into its leading name and the rest, or null when the label has no separator. */
export function splitLeadingName(label: string): { lead: string; rest: string } | null {
  const m = LEADING_SEPARATOR.exec(label.trim());
  return m && m[1].trim() && m[2].trim() ? { lead: m[1].trim(), rest: m[2].trim() } : null;
}

/** Why a row's suggestion was made, for the tag beside it. */
export type SuggestionReason = "SameKey" | "SameName" | "LeadingName";

export interface StructureSuggestion {
  choice: StructureChoice;
  reason: SuggestionReason;
}

type TargetList = { sectionKey: string; segmentKey: string; label: string };
type TargetItem = TargetList & { itemKey: string; itemLabel: string };

function flatten(targets: StructureTargetSection[]) {
  const lists: TargetList[] = [];
  const items: TargetItem[] = [];
  for (const s of targets) {
    for (const g of s.segments) {
      lists.push({ sectionKey: s.key, segmentKey: g.key, label: g.label });
      for (const i of g.items) items.push({ sectionKey: s.key, segmentKey: g.key, label: g.label, itemKey: i.key, itemLabel: i.label });
    }
  }
  return { lists, items };
}

/** The one candidate to suggest, or none when there are several equally good ones (a guess between two is worse than none). */
function only<T>(xs: T[], prefer?: (x: T) => boolean): T | null {
  if (xs.length === 1) return xs[0];
  if (prefer) {
    const preferred = xs.filter(prefer);
    if (preferred.length === 1) return preferred[0];
  }
  return null;
}

/**
 * A suggested answer per row, never saved until the dial admin saves. Tried in order, first hit wins:
 * 1. the same key at the same level in the new version (a list moved to another section keeps its key);
 * 2. a node at the same level with the same name;
 * 3. a leading-name match: "Dentist · Mila" is kept as "Mila" under the list "Dentist" (an item), or under the section
 *    "Dentist" (a list). An item whose WHOLE name is a list's ("Dentist") is kept, named as it is, under that list;
 * 4. nothing.
 * Two rows are never suggested into the same node (the server refuses that), and items under a list that has a suggestion
 * get none, because the list carries them.
 */
export function suggestStructureMap(atRisk: StructureAtRiskNode[], targets: StructureTargetSection[]): Record<string, StructureSuggestion> {
  const { lists, items } = flatten(targets);
  const out: Record<string, StructureSuggestion> = {};
  const taken = new Set<string>();
  const take = (sectionKey: string, segmentKey: string, itemKey?: string | null) => {
    const k = `${sectionKey}/${segmentKey}/${itemKey ?? ""}`;
    if (taken.has(k)) return false;
    taken.add(k);
    return true;
  };

  for (const n of atRisk.filter((x) => !isItem(x))) {
    const id = structureRowId(n);
    const sameKey = only(lists.filter((l) => l.segmentKey === n.segmentKey), (l) => l.sectionKey === n.sectionKey);
    if (sameKey && take(sameKey.sectionKey, sameKey.segmentKey)) {
      out[id] = { reason: "SameKey", choice: { action: "MoveTo", sectionKey: sameKey.sectionKey, segmentKey: sameKey.segmentKey } };
      continue;
    }
    const sameName = only(lists.filter((l) => norm(l.label) === norm(n.label)));
    if (sameName && take(sameName.sectionKey, sameName.segmentKey)) {
      out[id] = { reason: "SameName", choice: { action: "MoveTo", sectionKey: sameName.sectionKey, segmentKey: sameName.segmentKey } };
      continue;
    }
    const split = splitLeadingName(n.label);
    const section = split ? only(targets.filter((s) => norm(s.label) === norm(split.lead))) : null;
    if (split && section) {
      out[id] = { reason: "LeadingName", choice: { action: "KeepAsCustomUnder", sectionKey: section.key, newName: split.rest } };
    }
  }

  for (const n of atRisk.filter(isItem)) {
    const id = structureRowId(n);
    if (out[listRowId(n)]) continue;
    const sameKey = only(items.filter((i) => i.itemKey === n.itemKey), (i) => i.segmentKey === n.segmentKey);
    if (sameKey && take(sameKey.sectionKey, sameKey.segmentKey, sameKey.itemKey)) {
      out[id] = { reason: "SameKey", choice: { action: "MoveTo", sectionKey: sameKey.sectionKey, segmentKey: sameKey.segmentKey, itemKey: sameKey.itemKey } };
      continue;
    }
    const sameName = only(items.filter((i) => norm(i.itemLabel) === norm(n.label)));
    if (sameName && take(sameName.sectionKey, sameName.segmentKey, sameName.itemKey)) {
      out[id] = { reason: "SameName", choice: { action: "MoveTo", sectionKey: sameName.sectionKey, segmentKey: sameName.segmentKey, itemKey: sameName.itemKey } };
      continue;
    }
    const split = splitLeadingName(n.label);
    const lead = split?.lead ?? n.label;
    const list = only(lists.filter((l) => norm(l.label) === norm(lead)));
    if (list) {
      out[id] = { reason: "LeadingName", choice: { action: "KeepAsCustomUnder", sectionKey: list.sectionKey, segmentKey: list.segmentKey, newName: split?.rest ?? null } };
    }
  }

  return out;
}

/**
 * The table's starting selection: what's saved, and a suggestion for every row with no saved answer that the saved map doesn't
 * already cover through its list. Suggestions are only ever shown; nothing is saved until the dial admin saves.
 */
export function initialStructureSelection(
  atRisk: StructureAtRiskNode[], saved: StructureRemapEntry[], suggestions: Record<string, StructureSuggestion>,
): StructureSelection {
  const out = selectionFromMap(saved);
  for (const n of atRisk) {
    const id = structureRowId(n);
    if (out[id] || (isItem(n) && out[listRowId(n)])) continue;
    const s = suggestions[id];
    if (s) out[id] = s.choice;
  }
  return out;
}

/** Whether two choices say the same thing (to tag a row "Suggested" only while it still is the suggestion). */
export function sameChoice(a: StructureChoice | undefined, b: StructureChoice | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// ---- Refusals -------------------------------------------------------------------------------------------------------------

const PROBLEM_WORDS: Record<StructureRemapProblemReason, string> = {
  NotAtRisk: "This no longer holds anything, so it doesn't need a choice. Clear it and save again.",
  Duplicate: "This was chosen twice. Choose once and save again.",
  UnknownAction: "That choice wasn't recognised. Choose again.",
  MissingTarget: "Choose where this should go.",
  TargetNotInNewVersion: "The place you chose isn't in the new version. Choose another.",
  LevelMismatch: "This can't go there. Choose another place.",
  ManyToOne: "Something else is already moving there. Choose a different place, or keep it as your own.",
  Conflict: "Where you chose already has its own records, which this would overwrite. Choose somewhere else, or keep it as your own.",
};

/** A refused save's problems as friendly lines per row. The server's `detail` is for engineers and is never shown. */
export function refusalByRow(problems: StructureRemapProblem[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of problems) {
    out[structureRowId(p)] = PROBLEM_WORDS[p.reason] ?? "This choice can't be used. Choose again.";
  }
  return out;
}

/**
 * "Everything in it joins …": the target's name as the dial admin reads it, "Healthcare › Dentist › Mila", or "8. Christmas"
 * for a list that shares its section's name or is the section's only list (see {@link placeName}).
 */
export function targetLabel(targets: StructureTargetSection[], c: StructureChoice): string | null {
  if (c.action === "Discard") return null;
  const section = targets.find((s) => s.key === c.sectionKey);
  if (!section) return null;
  if (!c.segmentKey) return section.label;
  const list = section.segments.find((g) => g.key === c.segmentKey);
  if (!list) return null;
  const place = placeName(section.label, list.label, section.segments.length === 1);
  if (c.action !== "MoveTo" || !c.itemKey) return place;
  const item = list.items.find((i) => i.key === c.itemKey);
  return item ? `${place} › ${item.label}` : null;
}

/** One option in the target picker: its value ("section/list/item") and its full, readable name. */
export interface StructureTargetOption {
  value: string;
  label: string;
}

/**
 * The picker's options for a row, as ONE flat list in section order then list order, every option carrying its full name
 * ("5. Home & vehicles › Chores", "8. Christmas"). No groups: a native select draws group headers as greyed, unselectable
 * rows, which left the only readable text on the one thing that can't be picked.
 * A list moves into a list and is kept in a section; an item moves into an item and is kept in a list.
 */
export function targetOptions(targets: StructureTargetSection[], n: NodePath, action: "MoveTo" | "KeepAsCustomUnder"): StructureTargetOption[] {
  const mine = (custom: boolean) => (custom ? " (yours)" : "");
  const out: StructureTargetOption[] = [];
  for (const s of targets) {
    if (action === "KeepAsCustomUnder" && !isItem(n)) {
      out.push({ value: `${s.key}//`, label: `${s.label}${mine(s.custom)}` });
      continue;
    }
    for (const g of s.segments) {
      const place = placeName(s.label, g.label, s.segments.length === 1);
      if ((action === "MoveTo" && !isItem(n)) || (action === "KeepAsCustomUnder" && isItem(n))) {
        out.push({ value: `${s.key}/${g.key}/`, label: `${place}${mine(g.custom)}` });
        continue;
      }
      for (const i of g.items) out.push({ value: `${s.key}/${g.key}/${i.key}`, label: `${place} › ${i.label}${mine(i.custom)}` });
    }
  }
  return out;
}

